import {create} from 'zustand'
import {useShallow} from 'zustand/react/shallow'
import {immer} from 'zustand/middleware/immer'
import {persist, createJSONStorage} from 'zustand/middleware'
import {createTtlStorage} from '../utils/ttlStorage'
import {FILTERS_TTL_MS} from './ui.slice'
import {EShift} from '../types/app.type'

// Client-Overview-dashboard filter set. Independent from the ui store's shared
// project-view filters on purpose: selections made on the dashboard must not
// leak into the activity list (and vice versa), and clearAllFilters must not
// touch this set. `null` means "use the default computed at render time"
// (first network of the client / current month) — storing the computed default
// would go stale, and keeping it out of the store keeps date logic out too.
interface DashboardFiltersState {
  dashboardPeriod: string[] | null
  dashboardTeamIds: string[]
  dashboardActivityTypeIds: string[]
  dashboardStatuses: string[]
  dashboardShift: EShift
  setDashboardPeriod: (dates: string[]) => void
  toggleDashboardTeamId: (id: string) => void
  toggleDashboardActivityTypeId: (id: string) => void
  toggleDashboardStatus: (id: string) => void
  setDashboardShift: (shift: EShift) => void
  clearDashboardFilters: () => void
}

const toggleIn = (list: string[], id: string) =>
  list.includes(id) ? list.filter(x => x !== id) : [...list, id]

export const useDashboardStore = create<DashboardFiltersState>()(
  persist(
    immer(set => ({
      dashboardPeriod: null,
      dashboardTeamIds: [],
      dashboardActivityTypeIds: [],
      dashboardStatuses: [],
      // Default 'both' = no shift filter, showing day and night activities.
      dashboardShift: 'both',

      // The expanded day list (InputMultiDateField's contract), already clamped
      // by the screen (max 31 days, backend limit).
      setDashboardPeriod: (dates: string[]) => {
        set(state => {
          state.dashboardPeriod = dates
        })
      },

      toggleDashboardTeamId: (id: string) => {
        set(state => {
          state.dashboardTeamIds = toggleIn(state.dashboardTeamIds, id)
        })
      },

      toggleDashboardActivityTypeId: (id: string) => {
        set(state => {
          state.dashboardActivityTypeIds = toggleIn(
            state.dashboardActivityTypeIds,
            id,
          )
        })
      },

      toggleDashboardStatus: (id: string) => {
        set(state => {
          state.dashboardStatuses = toggleIn(state.dashboardStatuses, id)
        })
      },

      setDashboardShift: (shift: EShift) => {
        set(state => {
          state.dashboardShift = shift
        })
      },

      // Atomic reset of the whole dashboard set — the ResetFiltersButton's
      // 'dashboard' scope action. Defaults re-derive at render time.
      clearDashboardFilters: () => {
        set(state => {
          state.dashboardPeriod = null
          state.dashboardTeamIds = []
          state.dashboardActivityTypeIds = []
          state.dashboardStatuses = []
          state.dashboardShift = 'both'
        })
      },
    })),
    {
      name: 'visualean-dashboard-filters',
      // Same 12h TTL semantics as the ui store's filters: a refresh/new tab the
      // same day resumes the set; past 12h it expires back to the defaults.
      storage: createJSONStorage(() =>
        createTtlStorage(FILTERS_TTL_MS, localStorage),
      ),
      partialize: state => ({
        dashboardPeriod: state.dashboardPeriod,
        dashboardTeamIds: state.dashboardTeamIds,
        dashboardActivityTypeIds: state.dashboardActivityTypeIds,
        dashboardStatuses: state.dashboardStatuses,
        dashboardShift: state.dashboardShift,
      }),
      // Version stays 1: dashboardShift is a new optional key. On hydration the
      // default merge back-fills it from the initial state ('both') for an older
      // persisted set. Bumping the version WITHOUT a migrate fn would instead
      // discard the whole persisted set (zustand treats a version mismatch with
      // no migrator as unmigratable), wiping every existing filter — so don't.
      version: 1,
    },
  ),
)

// Granular selector hooks — one per value, so consumers only re-render when
// the value they read changes.
export const useDashboardPeriod = () =>
  useDashboardStore(state => state.dashboardPeriod)

export const useDashboardTeamIds = () =>
  useDashboardStore(state => state.dashboardTeamIds)

export const useDashboardActivityTypeIds = () =>
  useDashboardStore(state => state.dashboardActivityTypeIds)

export const useDashboardStatuses = () =>
  useDashboardStore(state => state.dashboardStatuses)

export const useDashboardShift = () =>
  useDashboardStore(state => state.dashboardShift)

export const useDashboardActions = () =>
  useDashboardStore(
    useShallow(state => ({
      setDashboardPeriod: state.setDashboardPeriod,
      toggleDashboardTeamId: state.toggleDashboardTeamId,
      toggleDashboardActivityTypeId: state.toggleDashboardActivityTypeId,
      toggleDashboardStatus: state.toggleDashboardStatus,
      setDashboardShift: state.setDashboardShift,
      clearDashboardFilters: state.clearDashboardFilters,
    })),
  )
